'use client';

import { useMemo } from 'react';

import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import {
  GraphQLCommitment,
  GraphQLCommitmentType,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { commitmentsQuery, givingTotalsQuery } from '../queries.ts';
import {
  filterSponsorshipCommitments,
  sortCommitmentsByPriority,
  sortSponsorshipCommitmentsByParticipantName,
} from '../utils/commitments.ts';
import { useSupporterQuery } from './base.ts';

export const useCommitmentSortByPriority = () => {
  const { data: commitments = [] } = useSupporterQuery(commitmentsQuery);

  const groupedCommitments = useMemo(() => {
    return sortCommitmentsByPriority(commitments);
  }, [commitments]);

  return groupedCommitments;
};

export const useSponsorships = () => {
  const queryResult = useSupporterQuery(commitmentsQuery);
  const commitments = queryResult.data ?? [];

  const sponsorships = useMemo(() => {
    return sortSponsorshipCommitmentsByParticipantName(
      filterSponsorshipCommitments(commitments),
    );
  }, [commitments]);

  return {
    ...queryResult,
    sponsorships,
  };
};

/**
 * @returns {
 *  amountDue,
 *  hasMissedPayment,
 *  hasDueThisMonth,
 *  alertCommitments,
 *  missedPaymentCommitments,
 *  amountDueCommitments,
 *  payAheadCommitments
 * }
 */
export const useMakeAPaymentCommitments = (): {
  amountDue: number;
  hasMissedPayment: boolean;
  hasDueThisMonth: boolean;
  canPayAhead: boolean;
  alertCommitments: GraphQLCommitment[];
  missedPaymentCommitments: GraphQLCommitment[];
  amountDueCommitments: GraphQLCommitment[];
  payAheadCommitments: GraphQLCommitment[];
} => {
  const {
    data: [commitments = [], givingSummary],
  } = useSupporterQuery([commitmentsQuery, givingTotalsQuery]);

  // TODO: think through commitments coming back faster than giving history.
  // How to handle showing the alert. Does the alert just pop up on screen.
  const { totalDueThisMonthAmount, totalMissedPaymentAmount } =
    givingSummary ?? {};
  const {
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments,
  } = useMemo(() => {
    return sortCommitmentsByPriority(commitments ?? []).reduce(
      (acc, c) => {
        if (c.status !== CommitmentStatusEnum.Active) {
          return acc;
        }

        if (c.hasMissedPayment) {
          acc.missedPaymentCommitments.push(c);
        } else if (c.hasDueThisMonth) {
          acc.amountDueCommitments.push(c);
        } else if (c.commitmentType !== GraphQLCommitmentType.GiftCommitment) {
          // We don't allow pay-ahead for gift commitments, but do for other commitments that may or may not be on autopay
          acc.payAheadCommitments.push(c);
        }

        return acc;
      },
      {
        missedPaymentCommitments: [] as GraphQLCommitment[],
        amountDueCommitments: [] as GraphQLCommitment[],
        payAheadCommitments: [] as GraphQLCommitment[],
      },
    );
  }, [totalDueThisMonthAmount, totalMissedPaymentAmount]);

  return {
    amountDue: (totalDueThisMonthAmount ?? 0) + (totalMissedPaymentAmount ?? 0),
    alertCommitments:
      missedPaymentCommitments.length > 0
        ? missedPaymentCommitments
        : amountDueCommitments,
    hasMissedPayment: missedPaymentCommitments.length > 0,
    hasDueThisMonth: amountDueCommitments.length > 0,
    canPayAhead: payAheadCommitments.length > 0,
    payAheadCommitments,
    amountDueCommitments,
    missedPaymentCommitments,
  };
};
